import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
export const ROOT_BASE = BACKEND_URL;

const TOKEN_KEY = 'ns_token';

export const tokenStorage = {
  get: () => {
    try {
      return (
        localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY)
      );
    } catch {
      return null;
    }
  },
  set: (token, remember = true) => {
    try {
      tokenStorage.clear();
      if (remember) localStorage.setItem(TOKEN_KEY, token);
      else sessionStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* ignore */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Inject bearer token in every request
api.interceptors.request.use((config) => {
  const t = tokenStorage.get();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// On 401: clean session & redirect to /acceder (sólo si no estamos ya allí)
let sessionExpiredHandler = null;
export const setOnSessionExpired = (fn) => {
  sessionExpiredHandler = fn;
};

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    if (status === 401 && !url.endsWith('/auth/login')) {
      tokenStorage.clear();
      if (typeof sessionExpiredHandler === 'function') {
        sessionExpiredHandler();
      }
    }
    return Promise.reject(error);
  }
);

export const unwrap = (response) => {
  const body = response?.data ?? {};
  if (body?.success === false) {
    const err = new Error(body?.message || body?.error || 'API error');
    err.code = body?.error || 'api_error';
    err.payload = body;
    throw err;
  }
  return body?.data ?? body;
};

export const apiMessage = (response) =>
  response?.data?.message || null;

// Convenience: extracts a user-friendly error from any axios/api error
export const friendlyError = (err) => {
  if (err?.payload?.message) return err.payload.message;
  const body = err?.response?.data;
  if (body?.message) return body.message;
  if (typeof body?.error === 'string') return body.error;
  return err?.message || 'Ha ocurrido un error inesperado.';
};

export const fetchHealth = async () => {
  const res = await api.get('/health');
  return unwrap(res);
};

export const fetchVersion = async () => {
  const res = await api.get('/version');
  return unwrap(res);
};

// ----------------------------- Fase 02: Sports / Clubs / Regions -----------------------------

export const fetchSports = async () => {
  const res = await api.get('/sports/');
  const data = unwrap(res);
  return data?.items || [];
};

export const fetchClubs = async (params = {}) => {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== ''
    )
  );
  const res = await api.get('/clubs/', { params: cleaned });
  return unwrap(res); // { items, total, page, page_size, pages }
};

export const fetchClubBySlug = async (slug) => {
  const res = await api.get(`/clubs/${encodeURIComponent(slug)}`);
  return unwrap(res);
};

export const fetchClubTeams = async (clubId) => {
  const res = await api.get(`/clubs/${encodeURIComponent(clubId)}/teams`);
  const data = unwrap(res);
  return data?.items || [];
};

export const fetchClubSponsors = async (clubId) => {
  const res = await api.get(`/clubs/${encodeURIComponent(clubId)}/sponsors`);
  const data = unwrap(res);
  return data?.items || [];
};

export const fetchRegionCountries = async () => {
  const res = await api.get('/regions/countries');
  return unwrap(res)?.items || [];
};

export const fetchRegionSubregions = async (country) => {
  const res = await api.get('/regions/regions', { params: country ? { country } : {} });
  return unwrap(res)?.items || [];
};

export const fetchRegionProvinces = async (params = {}) => {
  const res = await api.get('/regions/provinces', { params });
  return unwrap(res)?.items || [];
};

export const fetchRegionCities = async (params = {}) => {
  const res = await api.get('/regions/cities', { params });
  return unwrap(res)?.items || [];
};

// ----------------------------- Fase 03: Events -----------------------------

export const fetchEvents = async (params = {}) => {
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== ''
    )
  );
  const res = await api.get('/events/', { params: cleaned });
  return unwrap(res);
};

export const fetchEventBySlug = async (slug) => {
  const res = await api.get(`/events/${encodeURIComponent(slug)}`);
  return unwrap(res);
};

export const fetchClubEvents = async (clubId, params = {}) => {
  const res = await api.get(
    `/clubs/${encodeURIComponent(clubId)}/events`,
    { params }
  );
  return unwrap(res)?.items || [];
};

export const fetchTeamEvents = async (teamId, params = {}) => {
  const res = await api.get(
    `/teams/${encodeURIComponent(teamId)}/events`,
    { params }
  );
  return unwrap(res)?.items || [];
};

export const fetchSportEvents = async (sportId, params = {}) => {
  const res = await api.get(
    `/sports/${encodeURIComponent(sportId)}/events`,
    { params }
  );
  return unwrap(res)?.items || [];
};

// ----------------------------- Fase 04: Admin CRUD -----------------------------

export const fetchAdminDashboard = async () => {
  const res = await api.get('/admin/dashboard');
  return unwrap(res);
};

export const fetchAdminList = async (entity, params = {}) => {
  const res = await api.get(`/admin/${entity}/`, { params });
  return unwrap(res);
};

export const createAdminEntity = async (entity, payload) => {
  const res = await api.post(`/admin/${entity}/`, payload);
  return { data: unwrap(res), message: apiMessage(res) };
};

export const updateAdminEntity = async (entity, id, payload) => {
  const res = await api.patch(`/admin/${entity}/${id}`, payload);
  return { data: unwrap(res), message: apiMessage(res) };
};

export const deleteAdminEntity = async (entity, id) => {
  const res = await api.delete(`/admin/${entity}/${id}`);
  return { data: unwrap(res), message: apiMessage(res) };
};

export const fetchAdminAuditLogs = async (params = {}) => {
  const res = await api.get('/admin/audit-logs/', { params });
  return unwrap(res);
};

// Prompt 4C-B — inspección financiera READ-ONLY (super_admin).
export const fetchAdminPaymentInspect = async (paymentId) => {
  const res = await api.get(`/admin/payments/inspect/${paymentId}`);
  return unwrap(res);
};

export const fetchAdminRedsysConfig = async () => {
  const res = await api.get('/admin/redsys/config');
  return unwrap(res);
};


/**
 * Sube una imagen (JPG/PNG) al endpoint admin de uploads.
 * Devuelve { url, filename, kind, bytes }. `url` es relativa (`/api/uploads/...`).
 */
export const uploadAdminImage = async (file, kind = 'thumbnail') => {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', kind);
  const res = await api.post('/admin/uploads/image', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 30000,
  });
  return unwrap(res);
};

/**
 * Compone la URL absoluta de un asset. Si `value` ya es una URL completa
 * (http/https), se devuelve tal cual. Si es relativa (`/api/uploads/...`),
 * se prefija con el backend.
 */
export const buildAssetUrl = (value) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || /^data:/i.test(value)) return value;
  const base = BACKEND_URL || '';
  if (value.startsWith('/')) return `${base}${value}`;
  return `${base}/${value}`;
};

// ============================================================
// Fase 05A — Pagos, planes y entitlements
// ============================================================
export const formatMoney = (cents, currency = 'EUR', locale = 'es-ES') => {
  const n = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
};

export const fetchPublicPlans = async (params = {}) => {
  const res = await api.get('/plans', { params });
  return unwrap(res);
};

export const fetchPlanDetail = async (slugOrId) => {
  const res = await api.get(`/plans/${slugOrId}`);
  return unwrap(res);
};

export const checkEventAccess = async (eventId) => {
  const res = await api.get('/access/check', { params: { event_id: eventId } });
  return unwrap(res);
};

const _genIdempotencyKey = () =>
  `ns-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const startCheckoutForEvent = async (eventId, { idempotencyKey } = {}) => {
  const key = idempotencyKey || _genIdempotencyKey();
  const res = await api.post(`/checkout/event/${eventId}`, { idempotency_key: key });
  return { ...unwrap(res), _idempotency_key: key };
};

export const startCheckoutForPlan = async (
  planId, { idempotencyKey, clubId, consentAcceptedAt } = {},
) => {
  const key = idempotencyKey || _genIdempotencyKey();
  const body = { idempotency_key: key };
  if (clubId) body.club_id = clubId;
  if (consentAcceptedAt) body.consent_accepted_at = consentAcceptedAt;
  const res = await api.post(`/checkout/plan/${planId}`, body);
  return { ...unwrap(res), _idempotency_key: key };
};

export const getCheckoutSession = async (sessionId) => {
  const res = await api.get(`/checkout/session/${sessionId}`);
  return unwrap(res);
};

// Config pública mínima del checkout (nombre del provider ONE_TIME activo).
// Se usa solo para renderizar el aviso correcto en el paywall.
export const getCheckoutConfig = async () => {
  const res = await api.get('/checkout/config');
  return unwrap(res);
};


export const simulateCheckoutSession = async (sessionId, outcome) => {
  const res = await api.post(`/checkout/session/${sessionId}/simulate`, { outcome });
  return unwrap(res);
};

export const fetchMyPurchases = async () => {
  const res = await api.get('/users/me/purchases');
  return unwrap(res);
};

export const fetchMySubscriptions = async () => {
  const res = await api.get('/users/me/subscriptions');
  return unwrap(res);
};

export const cancelMySubscription = async (subscriptionId) => {
  const res = await api.post(`/users/me/subscriptions/${subscriptionId}/cancel`);
  return unwrap(res);
};

// Admin
export const fetchAdminMonetizationList = async (entity, params = {}) => {
  const res = await api.get(`/admin/${entity}/`, { params });
  return unwrap(res);
};

// ============================================================
// Fase 05B — Descargas (download products + entitlements)
// ============================================================
export const startCheckoutForDownload = async (productId, { idempotencyKey } = {}) => {
  const key = idempotencyKey || `dl-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const res = await api.post(`/checkout/download/${productId}`, { idempotency_key: key });
  return { ...unwrap(res), _idempotency_key: key };
};

export const checkDownloadAccess = async (productId) => {
  const res = await api.get(`/downloads/${productId}/access`);
  return unwrap(res);
};

export const checkDownloadAccessBySource = async (sourceType, sourceId) => {
  const res = await api.get('/access/check-download', {
    params: { source_type: sourceType, source_id: sourceId },
  });
  return unwrap(res);
};

export const fetchMyDownloads = async () => {
  const res = await api.get('/users/me/downloads');
  return unwrap(res);
};

export const fetchDownloadProductBySource = async (sourceType, sourceId) => {
  // Helper público: usa el endpoint de check-download para obtener el producto público brief.
  try {
    const res = await api.get('/access/check-download', { params: { source_type: sourceType, source_id: sourceId } });
    const data = unwrap(res);
    return data?.product || null;
  } catch {
    return null;
  }
};

// ----------------------------- Fase 02: Favorites -----------------------------

export const fetchMyFavorites = async () => {
  const res = await api.get('/users/me/favorites');
  return unwrap(res)?.items || [];
};

export const addFavoriteClub = async (clubId) => {
  const res = await api.post('/users/me/favorite-club', { club_id: clubId });
  return { data: unwrap(res), message: apiMessage(res) };
};

export const removeFavoriteClub = async (clubId) => {
  const res = await api.delete(
    `/users/me/favorite-club/${encodeURIComponent(clubId)}`
  );
  return { data: unwrap(res), message: apiMessage(res) };
};

// ----------------------------- Fase 06A: Videos VOD -----------------------------

export const fetchEventWatch = async (slug) => {
  const res = await api.get(`/events/${encodeURIComponent(slug)}/watch`);
  return unwrap(res);
};

export const fetchVideoPlayback = async (videoId) => {
  const res = await api.get(`/videos/${encodeURIComponent(videoId)}/playback`);
  return unwrap(res);
};

// Admin
export const adminListVideos = async (params = {}) => {
  const res = await api.get('/admin/videos/', { params });
  return unwrap(res);
};

export const adminCreateVideo = async (eventId, payload) => {
  const res = await api.post(`/admin/events/${encodeURIComponent(eventId)}/videos`, payload);
  return unwrap(res);
};

export const adminUpdateVideo = async (id, payload) => {
  const res = await api.patch(`/admin/videos/${encodeURIComponent(id)}`, payload);
  return unwrap(res);
};

export const adminDeleteVideo = async (id) => {
  const res = await api.delete(`/admin/videos/${encodeURIComponent(id)}`);
  return unwrap(res);
};

export const adminListVideoProviders = async (params = {}) => {
  const res = await api.get('/admin/video-providers/', { params });
  return unwrap(res);
};

export const adminCreateVideoProvider = async (payload) => {
  const res = await api.post('/admin/video-providers/', payload);
  return unwrap(res);
};

export const adminUpdateVideoProvider = async (id, payload) => {
  const res = await api.patch(`/admin/video-providers/${encodeURIComponent(id)}`, payload);
  return unwrap(res);
};

export const adminDeleteVideoProvider = async (id) => {
  const res = await api.delete(`/admin/video-providers/${encodeURIComponent(id)}`);
  return unwrap(res);
};

// ----- Fase 06A · Entrega 2: PPV de Contenido -----
export const startContentCheckout = async (videoId, idempotencyKey = null) => {
  const headers = idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {};
  const res = await api.post(`/checkout/content/${encodeURIComponent(videoId)}`,
    {}, { headers });
  return { data: unwrap(res), message: apiMessage(res) };
};

// ----- Entrega 3: descarga de Contenido -----
export const startContentDownloadCheckout = async (videoId, idempotencyKey = null) => {
  const headers = idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {};
  const res = await api.post(`/checkout/content/${encodeURIComponent(videoId)}/download`,
    {}, { headers });
  return { data: unwrap(res), message: apiMessage(res) };
};

export const fetchVideoDownload = async (videoId) => {
  const res = await api.post(`/videos/${encodeURIComponent(videoId)}/download`, {});
  return unwrap(res);
};

export const adminMoveVideo = async (videoId, direction) => {
  // direction: 'up' | 'down'
  const res = await api.post(`/admin/videos/${encodeURIComponent(videoId)}/move-${direction}`, {});
  return { data: unwrap(res), message: apiMessage(res) };
};


// ============================================================
// Fase 07 — Dashboard de usuario
// ============================================================
export const fetchDashboardMe = async () => {
  const res = await api.get('/dashboard/me');
  return unwrap(res);
};

export const fetchMyEvents = async () => {
  const res = await api.get('/users/me/events');
  return unwrap(res);
};

export const fetchMyVideos = async () => {
  const res = await api.get('/users/me/videos');
  return unwrap(res);
};

export const fetchPlayerProfileSummary = async () => {
  const res = await api.get('/users/me/player-profile-summary');
  return unwrap(res);
};

export const setPrimaryFavoriteClub = async (clubId) => {
  const res = await api.patch('/users/me/favorite-club', { club_id: clubId });
  return { data: unwrap(res), message: apiMessage(res) };
};

export const generateDownloadLink = async (entitlementId) => {
  const res = await api.post(
    `/me/downloads/${encodeURIComponent(entitlementId)}/generate-link`,
    {},
  );
  return unwrap(res);
};

// ============================================================
// Fase 08 — Player Profile (Entrega 1)
// ============================================================
export const fetchMyPlayerProfile = async () => {
  const res = await api.get('/users/me/player-profile');
  return unwrap(res);
};

export const createMyPlayerProfile = async (payload) => {
  const res = await api.post('/users/me/player-profile', payload);
  return { data: unwrap(res), message: apiMessage(res) };
};

export const updateMyPlayerProfile = async (payload) => {
  const res = await api.patch('/users/me/player-profile', payload);
  return { data: unwrap(res), message: apiMessage(res) };
};

export const uploadMyPlayerPhoto = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post('/users/me/player-profile/photo', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return { data: unwrap(res), message: apiMessage(res) };
};

export const deleteMyPlayerPhoto = async () => {
  const res = await api.delete('/users/me/player-profile/photo');
  return { data: unwrap(res), message: apiMessage(res) };
};

export const fetchSportsList = async () => {
  const res = await api.get('/sports');
  return unwrap(res);
};

export const fetchClubsBySport = async (sportId) => {
  const res = await api.get(`/sports/${encodeURIComponent(sportId)}/clubs`);
  return unwrap(res);
};

export const fetchTeamsByClub = async (clubId) => {
  const res = await api.get(`/clubs/${encodeURIComponent(clubId)}/teams`);
  return unwrap(res);
};

export const fetchPositionsBySport = async (sportId) => {
  const res = await api.get(`/sports/${encodeURIComponent(sportId)}/positions`);
  return unwrap(res);
};

// --- Admin player profiles (Fase 08 entrega 2) ---
export const adminListPlayerProfiles = async (params = {}) => {
  const res = await api.get('/admin/player-profiles', { params });
  return unwrap(res);
};
// Catálogos reutilizados por la vista jerárquica de fichas.
// No exponen datos nuevos: mismos endpoints ya usados por AdminClubs/Teams.
export const adminListSportsCatalog = async () => {
  const res = await api.get('/admin/sports/');
  return unwrap(res);
};
export const adminListClubsCatalog = async () => {
  const res = await api.get('/admin/clubs/');
  return unwrap(res);
};
export const adminListTeamsCatalog = async () => {
  const res = await api.get('/admin/teams/');
  return unwrap(res);
};
// Detalle de un usuario (endpoint admin ya existente). Se usa para leer el
// `profile_type` en el modal de confirmación de vinculación.
export const adminGetUser = async (userId) => {
  const res = await api.get(`/admin/users/${encodeURIComponent(userId)}`);
  return unwrap(res);
};
export const adminGetPlayerProfile = async (id) => {
  const res = await api.get(`/admin/player-profiles/${encodeURIComponent(id)}`);
  return unwrap(res);
};
export const adminCreatePlayerProfile = async (payload) => {
  const res = await api.post('/admin/player-profiles', payload);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminUpdatePlayerProfile = async (id, payload) => {
  const res = await api.patch(`/admin/player-profiles/${encodeURIComponent(id)}`, payload);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminUploadPlayerProfilePhoto = async (id, file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post(`/admin/player-profiles/${encodeURIComponent(id)}/photo`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminDeletePlayerProfilePhoto = async (id) => {
  const res = await api.delete(`/admin/player-profiles/${encodeURIComponent(id)}/photo`);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminUpdatePlayerProfileStatus = async (id, status) => {
  const res = await api.patch(`/admin/player-profiles/${encodeURIComponent(id)}/status`, { status });
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminUpdatePlayerProfileVisibility = async (id, payload) => {
  const res = await api.patch(`/admin/player-profiles/${encodeURIComponent(id)}/visibility`, payload);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminLinkUserToProfile = async (id, userId) => {
  const res = await api.post(`/admin/player-profiles/${encodeURIComponent(id)}/link-user`, { user_id: userId });
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminUnlinkUserFromProfile = async (id) => {
  const res = await api.post(`/admin/player-profiles/${encodeURIComponent(id)}/unlink-user`);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminListLinkSuggestions = async (params = {}) => {
  const res = await api.get('/admin/player-profile-link-suggestions', { params });
  return unwrap(res);
};
export const adminLinkSuggestion = async (id) => {
  const res = await api.post(`/admin/player-profile-link-suggestions/${encodeURIComponent(id)}/link`);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminDismissSuggestion = async (id) => {
  const res = await api.post(`/admin/player-profile-link-suggestions/${encodeURIComponent(id)}/dismiss`);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminDeletePlayerProfile = async (id) => {
  const res = await api.delete(`/admin/player-profiles/${encodeURIComponent(id)}`);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminSearchUnlinkedPlayers = async (q, limit = 10) => {
  const res = await api.get('/admin/players/search', { params: { q, limit } });
  return unwrap(res);
};

// =====================================================================
// Fase 08B — Plantillas públicas y directorio admin
// =====================================================================
export const fetchPublicClubTeams = async (clubSlug) => {
  const res = await api.get(`/public/clubs/${encodeURIComponent(clubSlug)}/teams`);
  return unwrap(res);
};
export const fetchPublicTeamRoster = async (clubSlug, teamSlug) => {
  const res = await api.get(`/public/clubs/${encodeURIComponent(clubSlug)}/teams/${encodeURIComponent(teamSlug)}/players`);
  return unwrap(res);
};
export const fetchPublicPlayerProfile = async (clubSlug, teamSlug, playerSlug) => {
  const res = await api.get(`/public/clubs/${encodeURIComponent(clubSlug)}/teams/${encodeURIComponent(teamSlug)}/players/${encodeURIComponent(playerSlug)}`);
  return unwrap(res);
};
export const adminGetTeamPlayerDirectory = async (clubId, teamId) => {
  const res = await api.get(`/admin/clubs/${encodeURIComponent(clubId)}/teams/${encodeURIComponent(teamId)}/player-directory`);
  return unwrap(res);
};
export const adminSetTeamPublicRoster = async (teamId, enabled) => {
  const res = await api.patch(`/admin/teams/${encodeURIComponent(teamId)}/public-roster`, { public_roster_enabled: !!enabled });
  return { data: unwrap(res), message: apiMessage(res) };
};

// =====================================================================
// Fase 09 — Highlights oficiales + Notificaciones internas
// =====================================================================
export const fetchMyOfficialHighlights = async () => {
  const res = await api.get('/users/me/official-highlights');
  return unwrap(res);
};
export const fetchNotifications = async ({ unreadOnly = false, limit = 30 } = {}) => {
  const res = await api.get('/users/me/notifications', { params: { unread_only: unreadOnly, limit } });
  return unwrap(res);
};
export const markNotificationRead = async (id) => {
  const res = await api.patch(`/users/me/notifications/${encodeURIComponent(id)}/read`);
  return unwrap(res);
};
export const markAllNotificationsRead = async () => {
  const res = await api.post('/users/me/notifications/read-all');
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminFetchEventHighlights = async (eventId) => {
  const res = await api.get(`/admin/events/${encodeURIComponent(eventId)}/official-highlights`);
  return unwrap(res);
};
export const adminCreateHighlightAssignments = async (videoId, assignments) => {
  const res = await api.post(`/admin/videos/${encodeURIComponent(videoId)}/player-assignments`, { assignments });
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminPatchHighlightAssignment = async (assignmentId, patch) => {
  const res = await api.patch(`/admin/highlight-player-assignments/${encodeURIComponent(assignmentId)}`, patch);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminDeleteHighlightAssignment = async (assignmentId) => {
  const res = await api.delete(`/admin/highlight-player-assignments/${encodeURIComponent(assignmentId)}`);
  return { data: unwrap(res), message: apiMessage(res) };
};

// =====================================================================
// Fase 06D — Descargas seguras (download_assets + Bunny signed URLs)
// =====================================================================
export const fetchVideoDownloadOptions = async (videoId) => {
  const res = await api.get(`/videos/${encodeURIComponent(videoId)}/download-options`);
  return unwrap(res);
};
export const fetchEventDownloadOptions = async (eventId) => {
  const res = await api.get(`/events/${encodeURIComponent(eventId)}/download-options`);
  return unwrap(res);
};
export const generateAssetDownloadLink = async (downloadAssetId) => {
  const res = await api.post(`/downloads/${encodeURIComponent(downloadAssetId)}/generate-link`, {});
  return { data: unwrap(res), message: apiMessage(res) };
};
export const buildDownloadLinkUrl = (token) => {
  const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  return `${baseUrl}/api/downloads/link/${encodeURIComponent(token)}`;
};

/**
 * Descarga real (guardado como archivo) de una URL cualquiera — resuelve
 * el problema de "el vídeo se abre en pantalla completa" cuando el server
 * no envía `Content-Disposition: attachment`. Descarga el binario a un
 * Blob y lo entrega al navegador vía anchor con atributo `download` (que
 * SÍ funciona con URLs blob same-origin, forzando "Guardar como archivo").
 *
 * Reutiliza los permisos que YA validó el endpoint que produjo `url`
 * (no re-comprueba entitlements aquí).
 */
export const forceDownloadFromUrl = async (url, filename = 'contenido') => {
  const resp = await fetch(url, { credentials: 'omit', redirect: 'follow' });
  if (!resp.ok) {
    throw new Error(`Descarga no disponible (HTTP ${resp.status}).`);
  }
  const blob = await resp.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
  return filename;
};

/**
 * Igual que `forceDownloadFromUrl` pero streaming con progreso 0-100 %.
 * onProgress(pct:number, status:'preparing'|'downloading'|'completed').
 * Sólo emite 100 % / 'completed' cuando el archivo se guardó realmente.
 * Si ocurre un fallo antes del 100 %, lanza (el llamador debe hacer release).
 */
export const downloadFromUrlWithProgress = async (
  url, filename = 'contenido', { onProgress, signal } = {}
) => {
  const emit = (pct, status) => {
    if (typeof onProgress === 'function') onProgress(pct, status);
  };
  emit(0, 'preparing');
  const resp = await fetch(url, { credentials: 'omit', redirect: 'follow', signal });
  if (!resp.ok) {
    throw new Error(`Descarga no disponible (HTTP ${resp.status}).`);
  }
  const totalHeader = resp.headers.get('content-length');
  const total = totalHeader ? Number(totalHeader) : 0;
  const reader = resp.body && resp.body.getReader ? resp.body.getReader() : null;
  let blob;
  if (reader) {
    const chunks = [];
    let received = 0;
    emit(0, 'downloading');
    // Lee stream y actualiza el progreso.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.byteLength;
      if (total > 0) {
        const pct = Math.min(99, Math.floor((received / total) * 100));
        emit(pct, 'downloading');
      }
    }
    blob = new Blob(chunks);
  } else {
    // Fallback sin streaming: sin porcentaje intermedio.
    emit(50, 'downloading');
    blob = await resp.blob();
  }
  // Guardado en disco
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
  emit(100, 'completed');
  return filename;
};

// Fase 06D · una compra = una descarga: confirmar/liberar consumo
export const confirmDownloadComplete = async (entitlementId) => {
  const res = await api.post(
    `/downloads/entitlements/${encodeURIComponent(entitlementId)}/complete`
  );
  return unwrap(res);
};

export const releaseDownloadLock = async (entitlementId) => {
  try {
    const res = await api.post(
      `/downloads/entitlements/${encodeURIComponent(entitlementId)}/release`
    );
    return unwrap(res);
  } catch {
    // best-effort — no propagar
    return null;
  }
};

// Legacy content_download entitlement — endpoints de complete/release
// usados por el flujo actual /videos/{id}/download.
export const confirmContentDownloadComplete = async (videoId) => {
  const res = await api.post(
    `/videos/${encodeURIComponent(videoId)}/download/complete`
  );
  return unwrap(res);
};

export const releaseContentDownload = async (videoId) => {
  try {
    const res = await api.post(
      `/videos/${encodeURIComponent(videoId)}/download/release`
    );
    return unwrap(res);
  } catch {
    return null;
  }
};

/**
 * Fase 06D · Fuerza la descarga real del archivo audiovisual de forma
 * client-side, evitando el "abrir en reproductor" que aparecería si
 * la Pull Zone de Bunny no tiene activo "Force Download URL".
 *
 * Reutiliza los endpoints existentes: /downloads/{asset_id}/generate-link
 * (validación de entitlement) y /downloads/link/{token} (302 → signed URL).
 * Descarga el binario en un Blob y lo entrega al navegador vía anchor
 * con atributo download, lo que garantiza el "Guardar archivo" nativo
 * sin necesidad del truco de botón derecho → Guardar vídeo.
 *
 * Devuelve el nombre final guardado. Lanza si el usuario no tiene derecho.
 */
export const triggerAssetDownload = async (downloadAssetId, fallbackFilename = 'contenido') => {
  const { data } = await generateAssetDownloadLink(downloadAssetId);
  // Fase 06D · Rama administrativa: el token exige Bearer al canjearse.
  // Reusamos el cliente HTTP autenticado para que el backend valide
  // identidad + rol. Tras recibir la URL firmada de Bunny, iniciamos la
  // descarga con el mismo helper que la rama comercial.
  if (data?.is_admin) {
    const res = await api.get(`/downloads/link/${encodeURIComponent(data.token)}`);
    const resolved = unwrap(res) || {};
    const signedUrl = resolved.redirect_url;
    if (!signedUrl) {
      throw new Error('No se pudo obtener la URL firmada de descarga.');
    }
    return forceDownloadFromUrl(signedUrl, resolved.file_name || data.file_name || fallbackFilename);
  }
  const url = buildDownloadLinkUrl(data.token);
  return forceDownloadFromUrl(url, data.file_name || fallbackFilename);
};

// Admin CRUD de download_assets
export const adminListDownloadAssets = async (params = {}) => {
  const res = await api.get('/admin/download-assets/', { params });
  return unwrap(res);
};
export const adminCreateDownloadAsset = async (payload) => {
  const res = await api.post('/admin/download-assets/', payload);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminPatchDownloadAsset = async (assetId, payload) => {
  const res = await api.patch(`/admin/download-assets/${encodeURIComponent(assetId)}`, payload);
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminSetDownloadAssetStatus = async (assetId, status) => {
  const res = await api.patch(`/admin/download-assets/${encodeURIComponent(assetId)}/status`, { status });
  return { data: unwrap(res), message: apiMessage(res) };
};
export const adminDeleteDownloadAsset = async (assetId) => {
  const res = await api.delete(`/admin/download-assets/${encodeURIComponent(assetId)}`);
  return { data: unwrap(res), message: apiMessage(res) };
};


// =====================================================================
// Fase 3A — Player Intelligence (lectura autorizada)
// =====================================================================
// Estos helpers NO devuelven nunca `raw_json` ni `source_raw`: el servidor
// filtra ambos antes de responder. La autorización final es server-side
// (PÚBLICO | PROPIETARIO | ADMIN); estas funciones sólo transportan la
// petición autenticada al backend.
export const fetchPlayerIntelligenceMatches = async (playerProfileId) => {
  const res = await api.get(
    `/player-intelligence/players/${encodeURIComponent(playerProfileId)}/matches`,
  );
  return unwrap(res);
};
export const fetchPlayerIntelligenceLatest = async (playerProfileId) => {
  const res = await api.get(
    `/player-intelligence/players/${encodeURIComponent(playerProfileId)}/matches/latest`,
  );
  return unwrap(res);
};
export const fetchPlayerIntelligenceMatch = async (playerProfileId, playerMatchId) => {
  const res = await api.get(
    `/player-intelligence/players/${encodeURIComponent(playerProfileId)}/matches/${encodeURIComponent(playerMatchId)}`,
  );
  return unwrap(res);
};
// Fase 3B — Importador Admin. `player_profile_id` es la identidad canónica
// NetScouters (elegida por el admin). El backend NO recalcula estadísticas:
// se limita a validar estructura, autorizar admin, filtrar `source_raw`,
// aplicar anti-duplicados y persistir. La normalización se ejecuta en el
// frontend con `normalizePlayerMatch()` existente.
export const adminImportPlayerMatch = async ({ playerProfileId, rawJson, normalizedData }) => {
  const res = await api.post('/admin/player-intelligence/player-matches', {
    player_profile_id: playerProfileId,
    raw_json: rawJson,
    normalized_data: normalizedData,
  });
  return { data: unwrap(res), message: apiMessage(res) };
};

// ============================================================
// Admin · Importación Excel de fichas de jugador
// ============================================================
export const adminImportPlayerPreview = async (file) => {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post('/admin/player-profiles/import/preview', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
};

export const adminImportPlayerConfirm = async (previewId) => {
  const res = await api.post('/admin/player-profiles/import/confirm', {
    preview_id: previewId,
  });
  return unwrap(res);
};


// ---------------------------------------------------------------
// Admin · Concesiones gratuitas de visualización (Fase 1 grants)
// ---------------------------------------------------------------
export const adminSearchUsers = async (q, limit = 10) => {
  const res = await api.get('/admin/users/search', { params: { q, limit } });
  return unwrap(res);
};

export const adminGrantVideoAccess = async (videoId, userIds, endsAt, reason) => {
  const res = await api.post(`/admin/videos/${encodeURIComponent(videoId)}/grants`, {
    user_ids: userIds,
    ends_at: endsAt,
    reason: reason || null,
  });
  return unwrap(res);
};

export const adminListVideoGrants = async (videoId) => {
  const res = await api.get(`/admin/videos/${encodeURIComponent(videoId)}/grants`);
  return unwrap(res);
};

export const adminRevokeGrant = async (grantId) => {
  const res = await api.delete(`/admin/grants/${encodeURIComponent(grantId)}`);
  return unwrap(res);
};

// ---------------------------------------------------------------
// Fase 2 · Admin · Premium de cortesía por usuario
// ---------------------------------------------------------------
export const adminGrantUserPremiumCourtesy = async (userId, { days, endsAt, reason } = {}) => {
  const body = {};
  if (typeof days === 'number' && days > 0) body.days = days;
  if (endsAt) body.ends_at = endsAt;
  if (reason) body.reason = reason;
  const res = await api.post(`/admin/users/${encodeURIComponent(userId)}/premium-grant`, body);
  return unwrap(res);
};

export const adminGetUserPremiumCourtesy = async (userId) => {
  const res = await api.get(`/admin/users/${encodeURIComponent(userId)}/premium-grant`);
  return unwrap(res);
};

export const adminRevokeUserPremiumCourtesy = async (userId) => {
  const res = await api.delete(`/admin/users/${encodeURIComponent(userId)}/premium-grant`);
  return unwrap(res);
};

// Fase 2A · Player Intelligence · Consolidado multi-partido.
export const fetchPlayerIntelligenceConsolidated = async (playerProfileId) => {
  const res = await api.get(`/player-intelligence/players/${encodeURIComponent(playerProfileId)}/consolidated`);
  return unwrap(res);
};
